import { Component, EventEmitter, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { ToastService } from '../../../services/toast.service';
import { UsernameValidatorDirective } from '../../../shared/directives/username-validator.directive';
import { EmailValidatorDirective } from '../../../shared/directives/email-validator.directive';

@Component({
  selector: 'app-enable-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, UsernameValidatorDirective, EmailValidatorDirective],
  templateUrl: './enable-modal.component.html',
  styleUrl: './enable-modal.component.css'
})
export class EnableModalComponent implements OnInit {
  @Output() close = new EventEmitter<void>();

  // Form objects
  usernameForm = { username: '', touched: false, isTaken: false, isValidating: false };
  emailForm = { email: '', touched: false };
  agreeToTerms = false;

  // State management
  currentStep: 'username' | 'email' | 'fund' = 'username';
  userData: any = null;
  isLoading = false;

  private usernameCheck$ = new Subject<string>();

  constructor(
    private api: ApiService,
    private auth: AuthService,
    private toast: ToastService
  ) {
    this.setupUsernameValidation();
  }

  private setupUsernameValidation() {
    this.usernameCheck$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(username => {
        if (username.length < 3) {
          this.usernameForm.isTaken = false;
          return [null];
        }
        this.usernameForm.isValidating = true;
        return this.api.validateUsername(username);
      })
    ).subscribe({
      next: (res: any) => {
        this.usernameForm.isValidating = false;
        if (res === null) return;
        this.usernameForm.isTaken = res.data === true;
      },
      error: () => {
        this.usernameForm.isValidating = false;
      }
    });
  }

  ngOnInit() {
    // Load current user data from auth service.
    this.userData = this.auth.getUserData() || {};

    const rawExistingUser = this.userData?.is_existing_user;
    const isExistingUser = typeof rawExistingUser === 'string'
      ? rawExistingUser.toLowerCase() === 'true'
      : rawExistingUser === true;

    // Show username step only for non-existing users.
    if (isExistingUser === true) {
      this.currentStep = this.userData?.user_type === 'WEB3' ? 'email' : 'fund';
      return;
    }

    const existingUsername = this.userData.user_name || this.userData.username || '';

    if (existingUsername && this.shouldPrefillUsername(existingUsername)) {
      this.usernameForm.username = existingUsername;
      
      // If the user already has a valid username, skip the username step!
      if (this.userData?.user_type === 'WEB3') {
        this.currentStep = 'email';
      } else {
        this.currentStep = 'fund';
      }
    }
  }

  /**
   * Web2 signup can receive a generated proxy wallet before a username exists.
   * Do not show wallet/address-looking values as the editable username.
   */
  private shouldPrefillUsername(username: string): boolean {
    const userType = (this.userData?.user_type || '').toString().toUpperCase();

    if (userType === 'WEB2') {
      return false;
    }

    const proxyWallet = this.userData?.proxy_wallet || '';
    const trimmedUsername = username.trim();

    return trimmedUsername !== proxyWallet && !this.isWalletLikeValue(trimmedUsername);
  }

  private isWalletLikeValue(value: string): boolean {
    return /^0x[a-fA-F0-9]{40}$/.test(value) || (value.startsWith('0x') && value.length > 25);
  }

  onUsernameInput(value: string): void {
    const trimmedValue = (value || '').trim();

    if (this.isWalletLikeValue(trimmedValue) || trimmedValue === this.userData?.proxy_wallet) {
      this.usernameForm.username = '';
      return;
    }

    this.usernameCheck$.next(trimmedValue);
  }

  canContinueUsername(isUsernameInvalid: boolean | null): boolean {
    return !isUsernameInvalid
      && !!this.usernameForm.username.trim()
      && this.agreeToTerms
      && !this.isLoading
      && !this.usernameForm.isTaken
      && !this.usernameForm.isValidating;
  }

  /**
   * Save username and proceed to next step
   */
  saveUsername() {
    // Note: Validation is primarily handled by appUsernameValidator in the template.
    if (!this.usernameForm.username.trim() || this.usernameForm.isTaken || this.usernameForm.isValidating) {
      return;
    }

    if (!this.agreeToTerms) {
      this.toast.error('Please agree to the Terms of use');
      return;
    }

    this.isLoading = true;
    const userId = this.userData?.id || this.userData?.user_id;

    if (!userId) {
      this.isLoading = false;
      this.toast.error('Unable to determine current user.');
      return;
    }

    this.api.updateProfile(
      { user_name: this.usernameForm.username.trim() },
      { user_id: userId }
    ).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.toast.success('Username set successfully');
        
        // Update userData with new username
        if (this.userData) {
          this.userData.user_name = this.usernameForm.username.trim();
        }

        // Move to next step: email for WEB3, enable-trading for WEB2
        if (this.userData?.user_type === 'WEB3') {
          this.currentStep = 'email';
        } else {
          this.closeEnableModal();
        }
      },
      error: (err) => {
        this.isLoading = false;
        const apiMessage = err?.error?.message || err?.error?.error || '';
        const isUsernameTaken =
          apiMessage === 'USERNAME_ALREADY_TAKEN' ||
          apiMessage?.toLowerCase?.().includes('username_already_taken');

        const errorMsg = isUsernameTaken
          ? 'This username is already taken. Please choose another one.'
          : (apiMessage || 'Failed to set username. Please try again.');
        this.toast.error(errorMsg);
      }
    });
  }

  /**
   * Save email address and proceed to fund step
   */
  saveEmail(emailControl: any) {
    if (emailControl.invalid) {
      this.emailForm.touched = true;
      this.toast.error(this.getEmailValidationMessage(emailControl.errors));
      return;
    }

    this.isLoading = true;
    const userId = this.userData?.id || this.userData?.user_id;

    this.api.updateEmailAddress(
      { email: this.emailForm.email.trim() },
      userId
    ).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.toast.success('Email saved successfully. Please verify your email from the Profile page');
        
        // Update userData with new email
        if (this.userData) {
          this.userData.email = this.emailForm.email.trim();
        }

        // Continue flow: close profile modal so enable-trading modal opens next.
        this.closeEnableModal();
      },
      error: (err) => {
        this.isLoading = false;
        const errorMsg = err?.error?.message || 'Failed to save email. Please try again.';
        this.toast.error(errorMsg);
      }
    });
  }

  /**
   * Skip email step and go directly to fund step
   */
  skipEmail() {
    this.closeEnableModal();
  }

  public getEmailValidationMessage(errors: any): string {
    if (!errors) return '';

    if (errors['required']) {
      return 'Email address is required';
    }
    if (errors['invalidConsecutiveChars']) {
      return 'Email cannot contain consecutive special characters (.. or --)';
    }
    if (errors['localPartInvalid']) {
      return 'Email name part cannot start or end with a dot';
    }
    if (errors['domainLabelInvalid']) {
      return 'Domain parts cannot start or end with a hyphen';
    }
    if (errors['emailPattern'] || errors['email']) {
      return 'Please enter a valid email address';
    }

    return 'Please enter a valid email address';
  }

  closeEnableModal() {
    this.close.emit();
  }
}
